/**
 * Workspace — the LSP's in-memory project model (v0.2).
 *
 * Holds per-file `ParsedFile` state, plus a workspace-wide `ResolvedModel`
 * built by combining all top-level declarations from every file and
 * running the resolver/validator/graph builder. Feature providers read
 * from this single source of truth.
 */

import { parse, parsePackageManifest, procedureDiagnostics } from "@archlang/engine";
import { isMarkdownPath } from "@archlang/viewer-core";
import type { Token } from "@archlang/engine";
import type {
  Diagnostic as ArchDiagnostic,
  PackageManifest,
  ParsedFile,
  QualifiedName,
  ResolvedPackage,
  SourceSpan,
  SpaceScopedUses,
} from "@archlang/engine";
import {
  applyCascade,
  buildBacklinkIndex,
  buildDescriptionIndex,
  IncrementalPackageCompiler,
  parseMarkdownDocument,
  synthesizeStubs,
  validate,
  type BacklinkIndex,
  type DependencyGraph,
  type DescriptionIndex,
  type DiagnosticCode,
  type DiagnosticEvidence,
  type MarkdownDocument,
  type ResolvedModel,
  type StubResult,
} from "@archlang/engine";
import { DEFAULT_CONFIG, type ArchlangConfig } from "./config.js";
import { computePolicyDiagnostics } from "./policy-diagnostics.js";
import { computeViewLintDiagnostics } from "./view-lint-diagnostics.js";
import { computeTestDiagnostics } from "./test-diagnostics.js";

export interface FileDiagnostic {
  readonly severity: "error" | "warning" | "todo" | "info" | "hint";
  readonly message: string;
  readonly span?: SourceSpan;
  /** Registered code from the engine's closed registry (diagnostics/codes.ts). */
  readonly code?: DiagnosticCode;
  /** Catalog key + params so the LSP renders the message in the editor's
   *  locale (InitializeParams.locale) via the engine's `diagnosticText()`. */
  readonly msgKey?: string;
  readonly params?: Readonly<Record<string, unknown>>;
  /** Secondary locations (e.g. the policy rule behind a violation) for a
   *  clickable jump. Each carries its own span (with `.file` = the target uri). */
  readonly relatedInformation?: readonly { readonly span: SourceSpan; readonly message: string }[];
  /** The model facts that PROVE the diagnostic — carried through to the LSP
   *  `data` payload so a client shows the proof without re-running analysis. */
  readonly evidence?: readonly DiagnosticEvidence[];
}

export interface FileState {
  readonly uri: string;
  readonly text: string;
  readonly version: number;
  readonly file: ParsedFile;
  readonly tokens: readonly Token[];
  readonly parseErrors: readonly FileDiagnostic[];
}

export interface ManifestFileState {
  readonly uri: string;
  readonly text: string;
  readonly manifest: PackageManifest;
  readonly tokens: readonly Token[];
  readonly parseErrors: readonly FileDiagnostic[];
}

/** Raw, untracked state for a markdown document (§4.15). The parsed
 *  `MarkdownDocument` is rebuilt on every reload so its enclosing space stays
 *  in sync with the in-memory manifest set. */
export interface MdFileState {
  readonly uri: string;
  readonly text: string;
  readonly version: number;
}

const EMPTY_RESOLVED: ResolvedModel = {
  types: [],
  registry: { kinds: new Map(), byId: new Map() },
  modules: [],
  processes: [],
  subprocesses: [],
  views: [],
};

export class Workspace {
  private readonly files = new Map<string, FileState>();
  /** Markdown documents (§4.15), keyed by URI. Tracked parallel to `.arch`
   *  files; threaded onto `model.documents` for cross-refs + backlinks. */
  private readonly mdFiles = new Map<string, MdFileState>();
  /** Parsed + space-corrected documents, rebuilt on every reload. */
  private documentList: readonly MarkdownDocument[] = [];
  /** Same documents, keyed by URI for per-file provider access. */
  private readonly docByUri = new Map<string, MarkdownDocument>();
  private resolved: ResolvedModel = EMPTY_RESOLVED;
  private cascaded: ResolvedModel | null = null;
  private descIndex: DescriptionIndex | null = null;
  private backlink: BacklinkIndex | null = null;
  private stubResult: StubResult | null = null;
  private graph: DependencyGraph | null = null;
  private diagnostics = new Map<string, FileDiagnostic[]>();
  private currentConfig: ArchlangConfig = DEFAULT_CONFIG;
  /** Parsed package.archspace, if any. URI → manifest. */
  private manifests = new Map<string, ManifestFileState>();
  /** Pre-resolved dependency packages (loaded externally by the LSP server). */
  private dependencies: Map<string, ResolvedPackage> = new Map();
  /** Incremental compiler — a single-file content edit re-resolves only the
   *  changed declaration instead of recompiling the whole workspace cold. */
  private readonly ipc = new IncrementalPackageCompiler(parse);
  private ipcSeeded = false;

  get config(): ArchlangConfig {
    return this.currentConfig;
  }

  setConfig(config: ArchlangConfig): void {
    this.currentConfig = config;
    // Config only remaps diagnostic severities — no recompile, just re-group the
    // existing compiled state through the new config.
    if (this.ipcSeeded) this.recomputeFromState();
  }

  setFile(uri: string, text: string, version: number = 0): void {
    if (uri.endsWith("/package.archspace") || uri.endsWith("\\package.archspace")) {
      this.setManifestFile(uri, text);
      return;
    }
    if (isMarkdownPath(uri)) {
      this.setMarkdownFile(uri, text, version);
      return;
    }
    const prev = this.files.get(uri);
    this.ingestArchFile(uri, text, version);

    // Incremental fast path: a content edit to a known file whose `use` set is
    // unchanged re-resolves only the changed declarations. Anything that can
    // shift the import/file surface (new file, changed `use`s) falls back to a
    // full reseed — same guard the server + browser use.
    const next = this.files.get(uri)!;
    if (this.ipcSeeded && prev && usesSig(prev.file) === usesSig(next.file)) {
      this.ipc.setFile(uri, text);
      this.recomputeFromState();
    } else {
      this.fullReload();
    }
  }

  /**
   * Batch seed — ingest a whole file set (`.arch` / `.md` / manifests mixed),
   * then rebuild ONCE. This is the bootstrap path: feeding a workspace file by
   * file runs a `fullReload` per markdown/manifest entry, and every reload
   * validates the whole model over every tracked document — quadratic in
   * documents, minutes on a docs-heavy monorepo. Interactive edits keep using
   * `setFile` (it has the incremental fast path a batch doesn't need).
   */
  setFiles(
    entries: readonly { uri: string; text: string; version?: number }[],
  ): void {
    if (entries.length === 0) return;
    for (const { uri, text, version } of entries) {
      if (uri.endsWith("/package.archspace") || uri.endsWith("\\package.archspace")) {
        this.ingestManifestFile(uri, text);
      } else if (isMarkdownPath(uri)) {
        this.mdFiles.set(uri, { uri, text, version: version ?? 0 });
      } else {
        this.ingestArchFile(uri, text, version ?? 0);
      }
    }
    this.fullReload();
  }

  /** Parse + track an `.arch` file without rebuilding. */
  private ingestArchFile(uri: string, text: string, version: number): void {
    const result = parse(text, uri);
    const parseErrors: FileDiagnostic[] = result.errors.map((e) => ({
      severity: "error",
      message: e.message,
      span: e.span,
      code: e.code,
      msgKey: e.msgKey,
      params: e.params,
    }));
    this.files.set(uri, {
      uri,
      text,
      version,
      file: result.file,
      tokens: result.tokens,
      parseErrors,
    });
  }

  /** Parse + track a manifest without rebuilding. */
  private ingestManifestFile(uri: string, text: string): void {
    const result = parsePackageManifest(text, uri);
    const parseErrors: FileDiagnostic[] = result.errors.map((e) => ({
      severity: "error",
      message: e.message,
      span: e.span,
      code: e.code,
      msgKey: e.msgKey,
      params: e.params,
    }));
    this.manifests.set(uri, {
      uri,
      text,
      manifest: result.manifest,
      tokens: result.tokens,
      parseErrors,
    });
  }

  /**
   * Track (or update) a markdown document (§4.15). A `.md` is never parsed as
   * `.arch` — it carries verbatim prose with the shared `[[ … ]]` / `@` ref
   * grammar. A document edit can shift cross-references/backlinks workspace-wide,
   * so it always takes the full-reload path (documents are not on the
   * incremental compiler's fast path).
   */
  setMarkdownFile(uri: string, text: string, version: number = 0): void {
    this.mdFiles.set(uri, { uri, text, version });
    this.fullReload();
  }

  setManifestFile(uri: string, text: string): void {
    this.ingestManifestFile(uri, text);
    this.fullReload();
  }

  /**
   * Inject a pre-resolved dependency package. The LSP server is expected
   * to load these (using the package-loader) and call this for each
   * dependency declared in our manifest.
   */
  setDependency(name: string, pkg: ResolvedPackage): void {
    this.dependencies.set(name, pkg);
    this.fullReload();
  }

  /** Swap the ENTIRE dependency set in one rebuild — the bootstrap path.
   *  `clearDependencies` + N × `setDependency` is N+1 full reloads. */
  replaceDependencies(deps: ReadonlyMap<string, ResolvedPackage>): void {
    if (this.dependencies.size === 0 && deps.size === 0) return;
    this.dependencies = new Map(deps);
    this.fullReload();
  }

  clearDependencies(): void {
    if (this.dependencies.size === 0) return;
    this.dependencies.clear();
    this.fullReload();
  }

  removeFile(uri: string): void {
    if (this.manifests.delete(uri)) {
      this.diagnostics.delete(uri);
      this.fullReload();
      return;
    }
    if (this.mdFiles.delete(uri)) {
      this.diagnostics.delete(uri);
      this.fullReload();
      return;
    }
    if (!this.files.has(uri)) return;
    this.files.delete(uri);
    this.diagnostics.delete(uri);
    this.fullReload();
  }

  /** Returns the manifest of the workspace's package, if any. */
  manifest(): PackageManifest | undefined {
    const first = this.manifests.values().next();
    return first.done ? undefined : first.value.manifest;
  }

  getFile(uri: string): FileState | undefined {
    return this.files.get(uri);
  }

  uris(): string[] {
    return Array.from(this.files.keys());
  }

  diagnosticsFor(uri: string): readonly FileDiagnostic[] {
    return this.diagnostics.get(uri) ?? [];
  }

  model(): ResolvedModel {
    return this.resolved;
  }

  /**
   * The model WITH synthesized stubs — the modules and interfaces a process
   * references but nobody declared (§4.13).
   *
   * `model()` deliberately stays RAW: completion has to know which names are
   * only implied so it can offer to declare them, and a diagnostic must never
   * point at a module the resolver invented. Navigation wants the opposite. A
   * process-first package is ALL implied modules, so a tree built on the raw
   * model renders EMPTY while the web viewer (which loads the stubbed model)
   * shows the full dashed skeleton — and the `synthesized` flag exists exactly
   * so a host can mark those rows rather than hide them.
   *
   * Shares `ghostPaths()`'s cache; both are lazy per rebuild.
   */
  stubbedModel(): ResolvedModel {
    if (!this.stubResult) this.stubResult = synthesizeStubs(this.resolved);
    return this.stubResult.model;
  }

  /**
   * Returns the resolved model with cascade/append propagation applied
   * (Spec §4.6). Lazily computed and cached per workspace rebuild.
   * Description rendering needs cascaded labels (Spec §4.11.3).
   */
  cascadedModel(): ResolvedModel {
    if (!this.cascaded) {
      this.cascaded = applyCascade(this.resolved);
    }
    return this.cascaded;
  }

  /** Description ref index over the cascaded model. Lazy + cached. Threads the
   *  tracked documents so `[[Doc#section]]` refs resolve (§4.15). */
  descriptionIndex(): DescriptionIndex {
    if (!this.descIndex) {
      this.descIndex = buildDescriptionIndex(this.cascadedModel(), this.documentList);
    }
    return this.descIndex;
  }

  /** All tracked markdown documents (§4.15), space-corrected. */
  documents(): readonly MarkdownDocument[] {
    return this.documentList;
  }

  /** The parsed document for a `.md` URI, if tracked. */
  getDocument(uri: string): MarkdownDocument | undefined {
    return this.docByUri.get(uri);
  }

  /** Raw, verbatim text of a tracked `.md` document (frontmatter included) —
   *  for cursor-offset math in hover / completion. */
  getDocumentText(uri: string): string | undefined {
    return this.mdFiles.get(uri)?.text;
  }

  /** URIs of every tracked markdown document. */
  documentUris(): string[] {
    return [...this.mdFiles.keys()];
  }

  /**
   * "Mentioned in" backlink index over descriptions + document bodies (§4.10 /
   * §4.15). Lazy + cached per workspace rebuild.
   */
  backlinkIndex(): BacklinkIndex {
    if (!this.backlink) {
      this.backlink = buildBacklinkIndex(this.cascadedModel(), this.documentList);
    }
    return this.backlink;
  }

  dependencyGraph(): DependencyGraph | null {
    return this.graph;
  }

  /**
   * "Ghost" entities (§4.13) — modules and interfaces a process references but
   * that are not declared anywhere. The resolver's stub synthesizer reports
   * exactly these as `modulePaths` / `interfacePaths`. The LSP indexes the raw
   * (un-stubbed) model, so completion uses this to offer names the author has
   * already implied by writing a flow but hasn't defined yet. Lazy + cached per
   * rebuild. Anonymous handler stand-ins (`(anonymous)`) are not real names and
   * are filtered out by callers.
   */
  ghostPaths(): { readonly modules: readonly string[]; readonly interfaces: readonly string[] } {
    if (!this.stubResult) this.stubResult = synthesizeStubs(this.resolved);
    return {
      modules: this.stubResult.modulePaths,
      interfaces: this.stubResult.interfacePaths,
    };
  }

  /** Types exported by every loaded dependency package: `{ name, pkg }`. Powers
   *  `use … from …` member completion (the importable surface of each dep). */
  exportedTypes(): { readonly name: string; readonly pkg: string }[] {
    const out: { name: string; pkg: string }[] = [];
    for (const [pkg, resolved] of this.dependencies) {
      for (const name of resolved.exports.keys()) out.push({ name, pkg });
    }
    return out;
  }

  /**
   * Map each `.arch` file URI to its enclosing space (v0.5) by matching it
   * against the directory of every `name:` (space) manifest, nearest (deepest)
   * wins. Mirrors viewer-core's disk walk but over the in-memory manifest set,
   * so live edits get cross-space diagnostics + space-aware hover without a
   * filesystem re-scan. Single-space packages produce an empty map (behavior
   * identical to pre-v0.5).
   */
  private buildSpaces(): Map<string, QualifiedName> {
    const dirs = this.spaceDirs();
    const spaces = new Map<string, QualifiedName>();
    if (dirs.length === 0) return spaces;
    for (const uri of this.files.keys()) {
      const s = this.nearestSpace(uri, dirs);
      if (s) spaces.set(uri, s);
    }
    return spaces;
  }

  /** Public read of the per-file space map — the symbol table's referrer side
   *  of space-aware resolution (v0.11). */
  fileSpaces(): ReadonlyMap<string, QualifiedName> {
    return this.buildSpaces();
  }

  /** Space-scoped imports (§4.11): every named-space manifest's `use` list,
   *  tagged with its space. Feeds the resolver so a space `use` is visible
   *  across that space's subtree (and no other). */
  private buildSpaceUses(): SpaceScopedUses[] {
    const out: SpaceScopedUses[] = [];
    for (const [, mf] of this.manifests) {
      const sn = mf.manifest.spaceName;
      if (sn && sn.length > 0 && mf.manifest.uses.length > 0) {
        out.push({ space: sn, uses: mf.manifest.uses });
      }
    }
    return out;
  }

  /** Directories of every named-space (`name:`) manifest, for nearest-first
   *  space resolution of both `.arch` files and `.md` documents. */
  private spaceDirs(): { dir: string; space: QualifiedName }[] {
    const out: { dir: string; space: QualifiedName }[] = [];
    for (const [uri, mf] of this.manifests) {
      const sn = mf.manifest.spaceName;
      if (sn && sn.length > 0) out.push({ dir: manifestDir(uri), space: sn });
    }
    return out;
  }

  /** Nearest (deepest) enclosing space for a URI, or undefined. */
  private nearestSpace(
    uri: string,
    dirs: { dir: string; space: QualifiedName }[]
  ): QualifiedName | undefined {
    let best: { dir: string; space: QualifiedName } | undefined;
    for (const sd of dirs) {
      if (uri === sd.dir || uri.startsWith(`${sd.dir}/`)) {
        if (!best || sd.dir.length > best.dir.length) best = sd;
      }
    }
    return best?.space;
  }

  /**
   * Parse + space-correct every tracked markdown document. The path-derived
   * space `parseMarkdownDocument` infers from a `file://` URI is meaningless, so
   * trust frontmatter `space:` first, else the nearest `.archspace` manifest,
   * else leave the document space-less.
   */
  private buildDocuments(): MarkdownDocument[] {
    const dirs = this.spaceDirs();
    const out: MarkdownDocument[] = [];
    for (const md of this.mdFiles.values()) {
      const parsed = parseMarkdownDocument(md.uri, md.text);
      const fmSpace = parsed.frontmatter.space ? parsed.space : undefined;
      const space = fmSpace ?? this.nearestSpace(md.uri, dirs);
      out.push({ ...parsed, space });
    }
    return out;
  }

  /**
   * Full (re)seed of the incremental compiler — manifest, dependencies, spaces,
   * and the complete file set. Used on first load and whenever the file set /
   * import surface / manifest / dependency tree changes (a content edit to an
   * existing file takes the incremental `setFile` path instead).
   */
  private fullReload(): void {
    const manifest = this.manifest();
    this.documentList = this.buildDocuments();
    this.docByUri.clear();
    for (const d of this.documentList) this.docByUri.set(d.path, d);
    this.ipc.load({
      name: manifest?.package,
      manifest,
      deps: this.dependencies,
      spaces: this.buildSpaces(),
      spaceUses: this.buildSpaceUses(),
      files: [...this.files.values()].map((f) => ({ uri: f.uri, content: f.text })),
      documents: this.documentList,
    });
    this.ipcSeeded = true;
    this.recomputeFromState();
  }

  /**
   * Pull the compiled model + graph + diagnostics out of the incremental
   * compiler and regroup diagnostics per file. The engine already cascades and
   * validates, so this is cheap (no recompile) — the same step a content edit
   * and a config change both finish with.
   */
  private recomputeFromState(): void {
    this.descIndex = null;
    this.backlink = null;
    this.stubResult = null;
    const s = this.ipc.state();
    this.resolved = s.rawModel; // raw (pre-cascade), matching the old `model()`
    this.cascaded = s.model; // engine-cascaded, matching `applyCascade(resolved)`
    this.graph = this.resolved.modules.length > 0 ? s.graph : null;

    // The engine surfaces PARSE_ERRORs too, but the LSP tracks its own per-file
    // parse errors (with its own spans) — drop the engine's to avoid duplicates.
    const compileDiags = s.diagnostics.filter((d) => d.code !== "PARSE_ERROR");
    // v0.10 policy findings (§4): evaluated over this package's model + graph and
    // merged before the config remap so `severity:` overrides apply to them too.
    // Smart-quiet: policies only bite on a COHERENT model — while the package has
    // ANY error-severity diagnostic (a resolve/validate error OR a per-file parse
    // error) the graph is partial, so policy findings would be noise. Suppress
    // them until the model is clean, then they surface.
    const modelIsBroken =
      compileDiags.some((d) => d.severity === "error") ||
      [...this.files.values()].some((f) => f.parseErrors.some((e) => e.severity === "error"));
    const policyDiags = modelIsBroken
      ? []
      : computePolicyDiagnostics(this.cascaded, this.graph, [...this.files.values()].map((f) => f.file), this.dependencies);
    // v0.10.3 BPMN lane-plan lints (§7): each `flow … bpmn` view's plan is
    // walked fresh-open here. Gated behind the same coherent-model check as
    // policies — a partial graph would false-lint empty lanes — which also
    // keeps the per-view walks off the hot path while the model is broken.
    const viewLintDiags = modelIsBroken
      ? []
      : computeViewLintDiagnostics(this.cascaded, [...this.files.values()].map((f) => f.file));
    // P7 process-test diagnostics: run each `test` over its process, surface
    // failing/OPEN assertions + unmatched-pin/no-target. Same coherent-model gate
    // as policies — a partial graph would false-report.
    const testDiags = modelIsBroken ? [] : computeTestDiagnostics(this.cascaded);
    // Light language packs (§language packs): each registered pack's procedures
    // run over the cascaded model, keyed to an aspect-key convention (e.g. a
    // telemetry pack flags `observed.p95 > slo.p95`). Same coherent-model gate as
    // policies — a partial graph would false-flag.
    const procedureDiags = modelIsBroken ? [] : procedureDiagnostics(this.cascaded);
    const adjusted = applyConfigToDiagnostics(
      [...compileDiags, ...policyDiags, ...viewLintDiags, ...testDiags, ...procedureDiags],
      this.currentConfig
    );
    const grouped = groupDiagnosticsByFile(adjusted);

    this.diagnostics.clear();
    for (const [uri, file] of this.files) {
      this.diagnostics.set(uri, [...file.parseErrors, ...(grouped.get(uri) ?? [])]);
    }
    for (const [uri, mf] of this.manifests) {
      this.diagnostics.set(uri, [...mf.parseErrors, ...(grouped.get(uri) ?? [])]);
    }

    // Markdown documents (§4.15): the incremental compiler resolves/validates
    // only `.arch`, so (a) it never emits document diagnostics, and (b) its
    // description index can't see documents, so a `[[Doc#section]]` ref in an
    // `.arch` description is wrongly flagged unresolved. Run the whole-model
    // validator once (its description index IS document-aware) and use it for
    // both: markdown files take its diagnostics wholesale; `.arch` files swap
    // in its document-aware description diagnostics for the document-blind set
    // the incremental path produced above.
    if (this.documentList.length > 0) {
      const full = applyConfigToDiagnostics(
        validate(this.cascadedModel()).diagnostics,
        this.currentConfig
      );
      const fullByFile = groupDiagnosticsByFile(full);
      for (const uri of this.mdFiles.keys()) {
        this.diagnostics.set(uri, fullByFile.get(uri) ?? []);
      }
      for (const uri of this.files.keys()) {
        const kept = (this.diagnostics.get(uri) ?? []).filter(
          (d) => !DESCRIPTION_DIAG_CODES.has(d.code ?? "")
        );
        const descAware = (fullByFile.get(uri) ?? []).filter((d) =>
          DESCRIPTION_DIAG_CODES.has(d.code ?? "")
        );
        this.diagnostics.set(uri, [...kept, ...descAware]);
      }
    }
  }
}

/** Description-class diagnostic codes — the only ones whose resolution depends
 *  on the document set (§4.10 / §4.15). Sourced from the document-aware
 *  whole-model validator when documents are present. */
const DESCRIPTION_DIAG_CODES = new Set([
  "DESC_UNRESOLVED_REF",
  "DESC_AMBIGUOUS_REF",
  "DESC_MISSING_ASPECT",
  "DOC_BARE_INTERPOLATION",
]);

/** Signature of a parsed file's `use` declarations — detects an import-surface
 *  change (which needs a full reseed, not an incremental edit). */
function usesSig(f: ParsedFile): string {
  return JSON.stringify(f.uses);
}

/** Directory URI of a `…/package.archspace` manifest (strip the filename). */
function manifestDir(manifestUri: string): string {
  const slash = manifestUri.lastIndexOf("/");
  return slash >= 0 ? manifestUri.slice(0, slash) : manifestUri;
}

function applyConfigToDiagnostics(
  diagnostics: readonly ArchDiagnostic[],
  config: ArchlangConfig
): ArchDiagnostic[] {
  const out: ArchDiagnostic[] = [];
  for (const diag of diagnostics) {
    const remappedSeverity = configSeverityFor(diag.code, config);
    if (remappedSeverity === "off") continue;
    if (remappedSeverity) {
      out.push({ ...diag, severity: remappedSeverity });
    } else {
      out.push(diag);
    }
  }
  return out;
}

function configSeverityFor(
  code: string | undefined,
  config: ArchlangConfig
): ArchDiagnostic["severity"] | "off" | undefined {
  if (!code) return undefined;
  const map: Record<string, keyof ArchlangConfig["validation"]> = {
    REQUIRED_NOT_FILLED: "missingTeam", // closest existing slot; refine when config schema updates
  };
  const key = map[code];
  if (!key) return undefined;
  const severity = config.validation[key];
  if (severity === "off") return "off";
  if (severity === "error" || severity === "warning" || severity === "info") {
    return severity;
  }
  return undefined;
}

function groupDiagnosticsByFile(
  diagnostics: readonly ArchDiagnostic[]
): Map<string, FileDiagnostic[]> {
  const grouped = new Map<string, FileDiagnostic[]>();
  for (const diag of diagnostics) {
    const uri = diag.source?.file;
    if (!uri) continue;
    const fileDiag: FileDiagnostic = {
      severity: diag.severity,
      message: diag.message,
      span: diag.source,
      code: diag.code,
      msgKey: diag.msgKey,
      params: diag.params,
      evidence: diag.evidence,
      relatedInformation: diag.relatedInformation?.map((r) => ({ span: r.source, message: r.message })),
    };
    const list = grouped.get(uri) ?? [];
    list.push(fileDiag);
    grouped.set(uri, list);
  }
  return grouped;
}
